import g from '#core/global';
import load from '#core/load';
import layout from '#core/layout';
import util from '#core/util';


const state = window.teal_state = window.teal_state || {};
state.masthead = state.masthead || {};

const marquee_$ = '.marquee';
const inner_$ = '.marquee__inner__inner';
const rail_$ = '.marquee__rail';
const group_$ = '.marquee__rail__group';
const toggle_$ = '.button--transport--marquee';

const is_playing_attr = 'data-playing';
const is_suspended_attr = 'data-suspended';
const speed_attr = 'data-speed';
const last_width_attr = 'data-last-width';
const marquee_ready_attr = 'data-marquee-ready';

const loop_time_prop = '--marquee-loop-time';
const group_width_prop = '--marquee-group-width';

let has_marquee = false;
let $marquees = null;
let previous_window_width = 0;


const module = {
	
	/**
	 * Init module
	 */
	init() {
		if (load.ran('marquee')) return false;
		
		load.content(() => {
			has_marquee = assign_elements();
			if (!has_marquee) return false;
			
			init_clone_groups();
		});
		load.all(() => {
			has_marquee = assign_elements();
			if (!has_marquee) return false;
			
			fire_clone_groups();
		});
	},
	
};

export default module;
module.init();


/**
 * Assign elements and event listeners
 * @return {boolean}
 */
function assign_elements() {
	if (has_marquee) return true;
	
	// TODO: do not store objects on load, account for dynamic content
	$marquees = document.querySelectorAll(marquee_$);
	if (!$marquees.length) return false;
	
	$marquees.forEach($marquee => {
		const $toggle = $marquee.querySelector(toggle_$);
		$toggle.addEventListener(g.click_e, e => {
			toggle_animation($marquee);
		});
		
		const $inner = $marquee.querySelector(inner_$);
		$inner.addEventListener(g.mouse_enter_e, e => {
			suspend_animation($marquee);
		});
		$inner.addEventListener(g.mouse_leave_e, e => {
			resume_animation($marquee);
		});
	});
	
	return true;
}


/**
 * Is this marquee currently playing?
 * @param {element} $marquee
 */
function is_playing($marquee) {
	return $marquee.getAttribute(is_playing_attr) === g.true_s;
}


/**
 * Toggle animation
 * @param {element} $marquee
 */
function toggle_animation($marquee) {
	$marquee.setAttribute(
		is_playing_attr,
		(is_playing($marquee))
			? g.false_s
			: g.true_s
	);
}


/**
 * Suspend animation momentarily
 * @param {element} $marquee
 */
function suspend_animation($marquee) {
	if (!is_playing($marquee)) return false;
	
	toggle_animation($marquee);
	$marquee.setAttribute(is_suspended_attr, g.true_s);
}


/**
 * Resume animation after momentary suspension
 * @param {element} $marquee
 */
function resume_animation($marquee) {
	const is_suspended = $marquee.getAttribute(is_suspended_attr) === g.true_s;
	if (!is_suspended) return false;
	
	toggle_animation($marquee);
	$marquee.setAttribute(is_suspended_attr, g.false_s);
}


/**
 * Clone groups on window resize or DOM changes
 */
function init_clone_groups() {
	fire_clone_groups();
	
	window.addEventListener(g.resize_e, util.throttle(() => {
		fire_clone_groups();
	}, 250));
	
	// Scope MutationObserver to individual marquees instead of entire document
	const observer = new MutationObserver(() => {
		fire_clone_groups();
	});
	$marquees.forEach($marquee => {
		observer.observe($marquee, {
			childList: true,
			attributes: true,
			attributeFilter: [is_playing_attr, is_suspended_attr, speed_attr],
		});
	});
}


/**
 * Clone groups on window resize or DOM changes
 */
function fire_clone_groups() {
	clone_groups();
	
	// Clone again, debounced to resolve layout issues
	// clearTimeout(clone_timeout);
	// clone_timeout = setTimeout(() => {
	// 	clone_groups();
	// }, 500);
}


/**
 * Clone groups to fill horizontal space
 */
function clone_groups() {
	const window_width = window.innerWidth;
	const has_window_width_changed = window_width !== previous_window_width;
	previous_window_width = window_width;
	
	$marquees.forEach($marquee => {
		// Safari doesn't want to actually show the marquee animation until
		// something on the page changes. This changes a CSS variable to a random
		// number, prompting Safari to show the marquee animation.
		$marquee.style.setProperty('--marquee-spline-reticulator', Math.random());
		
		const $rail = $marquee.querySelector(rail_$);
		const $groups = $marquee.querySelectorAll(group_$);
		const $first_group = $groups.item(0);
		const width = Math.round(layout.getWidth($first_group) * 100) / 100;
		
		// Cache width to avoid unnecessary recalculations (but don't skip if not ready yet)
		const last_width = parseFloat($marquee.getAttribute(last_width_attr)) || 0;
		const is_ready = $marquee.getAttribute(marquee_ready_attr) === g.true_s;
		if (
			width === last_width
			&& !has_window_width_changed
			&& is_ready
		) return;
		
		$marquee.setAttribute(last_width_attr, width);
		
		// Create as many clones as necessary to fill horizontal space
		if (width > 0) {
			const instance_count = Math.ceil((window_width * 2) / width) + 1;
			for (let i = 0; i < instance_count - $groups.length; i++) {
				const $clone = $first_group.cloneNode(true);
				
				// Remove any modals from the clone, as they should not be cloned,
				// otherwise we would have mutliple modals with the same ID
				$clone.querySelectorAll('.modal').forEach($modal => {
					$modal.remove();
				});
				
				$rail.appendChild($clone);
			}
			
			// Remove extraneous clones
			if ($groups.length > instance_count) {
				for (let i = $groups.length - 1; i >= instance_count; i--) {
					$groups.item(i).remove();
				}
			}
		}
		
		// TODO: factor window width into calculation, so animation runs slightly
		// slower on smaller viewports
		// const speed = $marquee.getAttribute(speed_attr);
		// const window_width_factor = 0.0005;
		// const window_ratio = window_width * window_width_factor;
		// const loop_time = Math.round(((width * (1 / window_ratio)) / (speed * 10)) * 10) / 10;
		
		const speed = $marquee.getAttribute(speed_attr);
		const traversal_time = window_width / (speed * 10);
		const loop_time = (width / window_width) * traversal_time;
		const loop_time_trunc = Math.round(loop_time * 100) / 100;
		
		if (loop_time_trunc > 0) {
			$marquee.style.setProperty(loop_time_prop, `${loop_time_trunc}s`);
		}
		if (width > 0) {
			$marquee.style.setProperty(group_width_prop, `${width}px`);
			
			// Mark marquee as ready once widths are calculated
			$marquee.setAttribute(marquee_ready_attr, g.true_s);
		}
	});
}
